import React, { useState } from 'react';
import {
  Paper, Box, Typography, Button, Popover,
  FormControl, InputLabel, Select, MenuItem, Slide,
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteIcon from '@mui/icons-material/Delete';
import LabelIcon from '@mui/icons-material/Label';
import DownloadIcon from '@mui/icons-material/Download';
import { useSnackbar } from '../contexts/SnackbarContext';

export default function BulkActionBar({
  selectedIds,
  onClearSelection,
  onBulkArchive,
  onBulkDelete,
  onBulkClassify,
  onBulkDownload,
}) {
  const { showSnack } = useSnackbar();
  const [classifyAnchor, setClassifyAnchor] = useState(null);
  const [classifyValue, setClassifyValue] = useState('unclassified');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleClassifyOpen = (e) => setClassifyAnchor(e.currentTarget);
  const handleClassifyClose = () => setClassifyAnchor(null);
  const handleClassifyApply = () => {
    onBulkClassify(classifyValue);
    handleClassifyClose();
  };

  const handleDeleteClick = () => {
    if (confirmDelete) {
      onBulkDelete();
      setConfirmDelete(false);
    } else {
      setConfirmDelete(true);
    }
  };

  const handleDownload = () => {
    showSnack('ZIP download coming soon', 'info');
    if (onBulkDownload) onBulkDownload();
  };

  return (
    <Slide direction="up" in={selectedIds.length > 0} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 1.5,
          borderRadius: 3,
          minWidth: { xs: 320, sm: 480 },
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="subtitle2" sx={{ mr: 1, whiteSpace: 'nowrap' }}>
          {selectedIds.length} document{selectedIds.length !== 1 ? 's' : ''} selected
        </Typography>

        <Button size="small" startIcon={<ArchiveIcon />} onClick={onBulkArchive} variant="outlined">
          Archive
        </Button>

        <Button size="small" startIcon={<LabelIcon />} onClick={handleClassifyOpen} variant="outlined">
          Classify
        </Button>

        <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload} variant="outlined">
          Download ZIP
        </Button>

        <Button
          size="small"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteClick}
          variant="outlined"
          color={confirmDelete ? 'error' : 'inherit'}
        >
          {confirmDelete ? 'Confirm Delete' : 'Delete'}
        </Button>

        {confirmDelete && (
          <Button size="small" onClick={() => setConfirmDelete(false)} variant="text">
            Cancel
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        <Button size="small" onClick={onClearSelection} variant="text" color="inherit">
          Clear
        </Button>

        <Popover
          open={Boolean(classifyAnchor)}
          anchorEl={classifyAnchor}
          onClose={handleClassifyClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Box sx={{ p: 2, minWidth: 220 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Set Classification</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Classification</InputLabel>
              <Select
                value={classifyValue}
                onChange={(e) => setClassifyValue(e.target.value)}
                label="Classification"
              >
                {['unclassified', 'internal', 'confidential', 'restricted', 'secret'].map((c) => (
                  <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button fullWidth variant="contained" size="small" sx={{ mt: 1.5 }} onClick={handleClassifyApply}>
              Apply
            </Button>
          </Box>
        </Popover>
      </Paper>
    </Slide>
  );
}
